
import React, { useEffect, useRef, useState } from "react";
import subsection_dots from "../../assets/images/subsection_dots.png";
import VennDiagram from "../../assets/styles/VennDiagram.tsx";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const OurSuccess = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const diagramRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Clean up function for ScrollTrigger instances
    const cleanup = () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };

    // Create animation timeline
    const setupAnimations = () => {
      if (!sectionRef.current || !headingRef.current || !diagramRef.current) return;

      // Set elements to be visible immediately for better UX
      setIsVisible(true);
      
      let timeline;
      const ctx = gsap.context(() => {
        // Initial state - already visible but will animate in
        gsap.set([headingRef.current, diagramRef.current], { opacity: 1 });
        
        // Immediate animation on component mount
        const tl = gsap.timeline();
        
        tl.fromTo(headingRef.current, 
          { y: 20, opacity: 0 }, 
          { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" }
        ).fromTo(diagramRef.current,
          { scale: 0.95, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.8, ease: "power2.out" },
          "-=0.3"
        );

        // Add scroll-based reveal for elements that are below the fold
        const isElementInViewport = (el) => {
          if (!el) return false;
          const rect = el.getBoundingClientRect();
          return (
            rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.bottom >= 0
          );
        };

        // If section is not in viewport on load, set up scroll animation
        if (!isElementInViewport(sectionRef.current)) {
          // Reset for scroll animation if not in viewport
          gsap.set([headingRef.current, diagramRef.current], { 
            opacity: 0,
            y: headingRef.current ? 20 : 0,
            scale: diagramRef.current ? 0.95 : 1
          });
          
          // Create scroll-triggered animation
          ScrollTrigger.create({
            trigger: sectionRef.current,
            start: "top 80%",
            onEnter: () => {
              gsap.to(headingRef.current, { 
                opacity: 1, 
                y: 0, 
                duration: 0.6, 
                ease: "power2.out" 
              });
              gsap.to(diagramRef.current, { 
                opacity: 1, 
                scale: 1, 
                duration: 0.8, 
                ease: "power2.out",
                delay: 0.2
              });
            },
            once: true
          });
        }
      }, sectionRef);

      return () => ctx.revert(); // cleanup context
    };

    // Run animation setup immediately
    const cleanup1 = setupAnimations();

    return () => {
      cleanup1 && cleanup1();
      cleanup();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-[60px] md:py-[80px] bg-[#FFFFFF] ">
      {/* Background dots - positioned absolutely */}
      <div className="absolute w-full inset-0 pointer-events-none overflow-hidden">
        <img 
          src={subsection_dots} 
          alt="" 
          className="object-cover"
          loading="eager"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Heading - visible by default for better UX */}
        <h2 
          ref={headingRef} 
          className={`text-3xl md:text-[44px] font-medium text-center mb-8 md:mb-12 text-gray-800 font-[LTWave] transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          What Drives Our Success
        </h2>

        {/* VennDiagram Container - visible by default for better UX */}
        <div 
          ref={diagramRef} 
          className={`relative w-full max-w-[320px] md:max-w-[550px] lg:max-w-[800px] h-[360px] md:h-[500px] lg:h-[700px] mx-auto transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <VennDiagram />
        </div>
      </div>
    </section>
  );
};

export default OurSuccess;